import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useConnections, useStatus, useVersion } from '../api/hooks.js';
import type { ToolCounts } from '../api/types.js';
import { Badge, Card, ConfirmDialog, PageTitle, SectionLabel } from '../components/ui.js';
import { Button } from '../components/ui/Button.js';
import { Select } from '../components/ui/Input.js';
import { daysUntilExpiry, getAiToken, markOnboardingDone } from '../lib/auth.js';
import { fmtTime, friendlyClient, minAgo } from '../lib/format.js';

const LOG_PAGE_SIZE = 20;

export function Dashboard() {
  const status = useStatus();
  const connections = useConnections();
  const version = useVersion();
  const queryClient = useQueryClient();
  const [filterClient, setFilterClient] = useState('');
  const [filterConn, setFilterConn] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const counts = useQuery({
    queryKey: ['tool-counts'],
    queryFn: () => api.get<ToolCounts>('/api/connections/tool-counts'),
  });

  const enabled = (connections.data ?? []).filter((c) => c.userEnabled !== false);
  const myConnected = enabled.filter((c) => c.userConnected || c.userApiKeySet || c.shared).length;
  if (myConnected > 0) markOnboardingDone();

  const failed = counts.data?._failed ?? [];
  const liveTotal = counts.data
    ? Object.entries(counts.data)
        .filter(([k]) => k !== '_failed')
        .reduce((s, [, d]) => s + (typeof d === 'object' && d && 'count' in d ? d.count : 0), 0)
    : 0;

  const sessions = status.data?.sessions ?? [];
  const calls = status.data?.recentCalls ?? [];
  const clients = [...new Set(calls.map((e) => e.clientName).filter(Boolean))] as string[];
  const conns = [...new Set(calls.map((e) => e.connector).filter(Boolean))];

  const filtered = useMemo(() => {
    let list = calls;
    if (filterClient) list = list.filter((e) => e.clientName === filterClient);
    if (filterConn) list = list.filter((e) => e.connector === filterConn);
    return list;
  }, [calls, filterClient, filterConn]);

  const visible = showAll ? filtered : filtered.slice(0, LOG_PAGE_SIZE);
  const aiDays = (() => {
    const t = getAiToken();
    return t ? daysUntilExpiry(t) : null;
  })();

  return (
    <div>
      <PageTitle title="Dashboard" sub="Tools, sessions and recent MCP calls." />

      {aiDays !== null && aiDays <= 7 ? (
        <div
          className="mb-4 flex items-center gap-3 rounded-lg px-4 py-3 text-sm"
          style={{
            background:
              aiDays <= 0
                ? 'color-mix(in srgb, var(--color-danger) 10%, transparent)'
                : 'color-mix(in srgb, var(--color-star) 12%, transparent)',
            border: `1px solid color-mix(in srgb, ${aiDays <= 3 ? 'var(--color-danger)' : 'var(--color-star)'} 30%, transparent)`,
          }}
        >
          <div className="flex-1">
            {aiDays <= 0
              ? 'Your AI token has expired. Cursor and Claude can no longer connect.'
              : `AI token expires in ${aiDays} day${aiDays === 1 ? '' : 's'}.`}
          </div>
          <Link to="/connect" className="text-sm font-medium text-[var(--color-accent)]">
            Renew in AI Config
          </Link>
        </div>
      ) : null}

      {myConnected === 0 && enabled.length > 0 ? (
        <Card className="mb-5 p-5">
          <div className="text-sm font-semibold">Welcome to Loom</div>
          <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
            Connect Memory and Mattermost, generate an AI token, then add it to Cursor.
          </p>
          <div className="mt-3 flex gap-2">
            <Link
              to="/connections"
              className="rounded-md px-3 py-1.5 text-sm font-medium text-white"
              style={{ background: 'var(--color-accent)' }}
            >
              Go to My Connections
            </Link>
            <Link to="/onboarding" className="rounded-md px-3 py-1.5 text-sm text-[var(--color-accent)]">
              Get Started guide
            </Link>
          </div>
        </Card>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          label="Total tools"
          value={counts.isLoading ? '…' : liveTotal}
          sub="across your connected tools"
        />
        <Stat label="Connectors online" value={myConnected} sub={`of ${enabled.length} configured`} />
        <Stat
          label="AI connection"
          value={sessions.length}
          sub={sessions.length === 0 ? 'not connected yet' : 'active session(s)'}
        />
        <Stat label="Calls logged" value={calls.length} sub="last 50 kept" />
      </div>

      <SectionLabel>Connectors</SectionLabel>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
        {enabled.map((c) => {
          const connected = c.userConnected || c.userApiKeySet || c.shared;
          const needsKey = c.supportsUserApiKey && !c.userApiKeySet && !connected;
          const pending = c.authType === 'oauth' && !connected;
          const detail = counts.data?.[c.name];
          const count = typeof detail === 'object' && detail && 'count' in detail ? detail.count : null;
          const isFailed = failed.includes(c.name);
          return (
            <Link key={c.name} to="/connections" className="no-underline">
              <Card className="p-4 transition-colors hover:bg-[var(--color-surface-2)]">
                <div className="text-sm font-semibold text-[var(--color-ink)]">{c.name}</div>
                <div className="mt-1 text-xs text-[var(--color-ink-faint)]">
                  {isFailed ? '? tools' : count !== null ? `${count} tool${count === 1 ? '' : 's'}` : '…'}
                </div>
                <div className="mt-2">
                  <Badge tone={connected ? 'good' : needsKey || pending ? 'warn' : 'bad'}>
                    {connected ? 'Connected' : needsKey ? 'Need API key' : pending ? 'Not connected' : 'Offline'}
                  </Badge>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>

      <SectionLabel>Your AI connection</SectionLabel>
      {sessions.length === 0 ? (
        <Card className="p-5">
          <div className="text-sm font-semibold">No AI tool connected yet</div>
          <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
            Add the Loom config to Cursor, Claude, or Codex to start using your tools.
          </p>
          <Link to="/connect" className="mt-3 inline-block text-sm font-medium text-[var(--color-accent)]">
            View AI Config
          </Link>
        </Card>
      ) : (
        <div className="grid gap-2 md:grid-cols-2">
          {sessions.map((s) => {
            const idle = s.lastSeen ? Date.now() - s.lastSeen >= 5 * 60000 : false;
            return (
              <Card key={s.id} className="p-4">
                <div className="flex items-center gap-2">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: idle ? 'var(--color-star)' : 'var(--color-accent)' }}
                  />
                  <span className="text-sm font-semibold">
                    {friendlyClient(s.clientInfo?.name)}
                    {s.clientInfo?.version ? ` ${s.clientInfo.version}` : ''}
                  </span>
                  <Badge tone={idle ? 'warn' : 'good'}>{idle ? 'Idle' : 'Active'}</Badge>
                </div>
                <div className="mt-2 text-xs text-[var(--color-ink-faint)]">
                  Connected: {s.connectedAt ? minAgo(s.connectedAt) : '—'}
                  <br />
                  Last active: {s.lastSeen ? minAgo(s.lastSeen) : '—'}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(s.liveConnectors && s.liveConnectors.length > 0
                    ? s.liveConnectors
                    : s.allowedConnectors
                  ).map((name) => (
                    <Badge key={name}>{name}</Badge>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <SectionLabel>Recent tool calls</SectionLabel>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} className="w-auto">
          <option value="">All AI tools</option>
          {clients.map((c) => (
            <option key={c} value={c}>
              {friendlyClient(c)}
            </option>
          ))}
        </Select>
        <Select value={filterConn} onChange={(e) => setFilterConn(e.target.value)} className="w-auto">
          <option value="">All connectors</option>
          {conns.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <Button variant="danger" size="sm" onClick={() => setConfirmClear(true)}>
          Clear
        </Button>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-[11px] tracking-wide text-[var(--color-ink-faint)] uppercase">
              <th className="px-3 py-2 font-semibold">#</th>
              <th className="px-3 py-2 font-semibold">Time</th>
              <th className="px-3 py-2 font-semibold">AI tool</th>
              <th className="px-3 py-2 font-semibold">Connector</th>
              <th className="px-3 py-2 font-semibold">Tool</th>
              <th className="px-3 py-2 font-semibold">Duration</th>
              <th className="px-3 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-sm text-[var(--color-ink-faint)]">
                  No tool calls yet — ask Cursor or Claude to use a tool
                </td>
              </tr>
            ) : (
              visible.map((e) => (
                <tr key={String(e.id)} className="border-t border-[var(--color-line)]">
                  <td className="px-3 py-2 text-xs text-[var(--color-ink-faint)]">{e.id}</td>
                  <td className="px-3 py-2 text-xs text-[var(--color-ink-faint)]">{fmtTime(e.ts)}</td>
                  <td className="px-3 py-2">
                    <Badge tone="accent">{friendlyClient(e.clientName)}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Badge>{e.connector}</Badge>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{e.tool}</td>
                  <td className="px-3 py-2 text-xs text-[var(--color-ink-faint)]">{e.durationMs}ms</td>
                  <td className="px-3 py-2 text-xs">
                    {e.success ? (
                      <span className="text-[var(--color-accent)]">ok</span>
                    ) : (
                      <span className="text-[var(--color-danger)]">{e.error ?? 'error'}</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
      {filtered.length > LOG_PAGE_SIZE ? (
        <div className="mt-2 text-center">
          <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)}>
            {showAll ? `Show less (${LOG_PAGE_SIZE} entries)` : `Show all ${filtered.length} entries`}
          </Button>
        </div>
      ) : null}

      {version.data?.version ? (
        <p className="mt-8 text-center text-xs text-[var(--color-ink-faint)]">
          Loom v{version.data.version} · Auto-refreshes every 5s
        </p>
      ) : null}

      {confirmClear ? (
        <ConfirmDialog
          title="Clear all tool call logs?"
          body="This will permanently delete all entries from the Recent Tool Calls panel."
          confirmText="Clear"
          onClose={() => setConfirmClear(false)}
          onConfirm={() => {
            void api.delete('/logs').then(() => {
              toast.success('Logs cleared');
              void queryClient.invalidateQueries({ queryKey: ['status'] });
            }).catch((err: Error) => toast.error(err.message));
            setConfirmClear(false);
          }}
        />
      ) : null}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string | number; sub: string }) {
  return (
    <Card className="p-4">
      <div className="text-[11px] tracking-wide text-[var(--color-ink-faint)] uppercase">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      <div className="mt-0.5 text-xs text-[var(--color-ink-faint)]">{sub}</div>
    </Card>
  );
}
